"use client";

import * as React from "react";
import MuxVideo from "@mux/mux-video-react";
import Image from "next/image";
import clsx from "clsx";
import { LazyMotion, domAnimation, m, type MotionProps } from "framer-motion";
import CtaButton from "@/components/ui/CtaButton";
import ButtonLandingPage from "@/components/landing/ButtonLandingPage";
import Breadcrumbs, { type BreadcrumbItem } from "./Breadcrumbs";

type Media = { playbackId: string; posterTime?: number };
type HeroImage = { src: string; alt: string };

type BaseProps = {
    breadcrumbs?: BreadcrumbItem[];
    title: string;
    description?: string;
    media?: Media;
    image?: HeroImage;
    overlayOpacity?: number;
    buttonTitle?: string;
    buttonHref?: string;
    useLandingButton?: boolean;
};

type ConflictingHtmlEvents = "onAnimationStart" | "onAnimationEnd" | "onAnimationIteration" | "onDrag" | "onDragStart" | "onDragEnd" | "onDragEnter" | "onDragLeave" | "onDragOver" | "onDrop";
type SafeSectionHtmlProps = Omit<React.ComponentPropsWithoutRef<"section">, keyof BaseProps | keyof MotionProps | ConflictingHtmlEvents>;

export type HeroDetailsServicesClientProps = BaseProps & SafeSectionHtmlProps & MotionProps;

// --- HELPERS ---

function shouldAvoidAutoplay(): boolean {
    if (typeof navigator === "undefined") return false;
    // @ts-ignore
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    return conn.saveData === true || conn.effectiveType === "slow-2g" || conn.effectiveType === "2g";
}

export default function HeroDetailsServicesClient(props: HeroDetailsServicesClientProps) {
    const {
        breadcrumbs,
        title,
        description = "",
        media,
        image,
        overlayOpacity = 0.35,
        buttonTitle = "NAPISZ DO NAS",
        buttonHref: _buttonHref,
        useLandingButton = false,
        className,
        ...rest
    } = props;

    // --- STATE ---
    const [hasVisited, setHasVisited] = React.useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = React.useState(false);

    // --- EFFECTS ---
    React.useEffect(() => {
        try {
            const visited = sessionStorage.getItem("hero_animated");
            if (visited) setHasVisited(true);
            else sessionStorage.setItem("hero_animated", "true");
        } catch (e) {}
    }, []);

    const avoidAutoplay = React.useMemo(() => shouldAvoidAutoplay(), []);
    const canAutoplay = Boolean(media?.playbackId) && !avoidAutoplay;
    const useVideo = Boolean(media?.playbackId);
    const useImage = !useVideo && Boolean(image?.src);
    const backgroundAlt = image?.alt || title;

    // --- MEMOS ---

    const posterUrl = React.useMemo(() => {
        if (!useVideo || !media?.playbackId) return undefined;
        const t = media.posterTime ?? 0;
        return `https://image.mux.com/${media.playbackId}/thumbnail.jpg?time=${encodeURIComponent(t)}&width=1920&fit_mode=preserve`;
    }, [media?.playbackId, media?.posterTime]);

    const streamUrl = React.useMemo(() => {
        if (!media?.playbackId) return undefined;

        const params = new URLSearchParams({
            min_resolution: "720p",
            rendition_order: "desc",
        });

        return `https://stream.mux.com/${media.playbackId}.m3u8?${params.toString()}`;
    }, [media?.playbackId]);

    const slideUp = React.useMemo(() => ({
        hidden: { y: 30, opacity: 0 },
        visible: (i: number) => ({
            y: 0, opacity: 1,
            transition: { duration: 0.6, ease: [0.215, 0.61, 0.355, 1], delay: hasVisited ? 0 : i * 0.1 + 0.4 },
        }),
    }), [hasVisited]);

    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="relume"
                initial="hidden"
                animate="visible"
                className={clsx("relative z-50 px-[5%] text-primary pt-14 md:pt-14 lg:pt-16", className)}
                {...rest}
            >
                <div className="container mt-14 md:mt-18">
                    <div className="relative">
                        <m.div
                            initial={{ clipPath: hasVisited ? "inset(0% 0% 0% 0%)" : "inset(0% 0% 100% 0%)" }}
                            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
                            transition={{ duration: 0.9, ease: [0.45, 0, 0.55, 1] }}
                            className="mx-auto w-full max-w-[1200px] md:max-w-[1400px] lg:max-w-[1600px] xl:max-w-[1800px] overflow-hidden rounded-3xl shadow-2xl ring-1 ring-black/5 bg-[#1a1a1a]"
                        >
                            <div className="relative bg-black overflow-hidden transform-gpu md:h-[clamp(480px,75vh,920px)] lg:h-[clamp(520px,82vh,980px)]">

                                {/* WARSTWA 1 (SPÓD): Wideo MUX */}
                                {canAutoplay && streamUrl && (
                                    <div className="absolute inset-0 h-full w-full z-10">
                                        <MuxVideo
                                            src={streamUrl}
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            preload="auto"
                                            onCanPlay={() => setIsVideoPlaying(true)}
                                            style={{
                                                height: "100%",
                                                width: "100%",
                                                objectFit: "cover",
                                                objectPosition: "center",
                                            }}
                                            className="h-full w-full"
                                        />
                                    </div>
                                )}

                                {/* WARSTWA 1 (SPÓD): Zdjęcie z Sanity — ten sam układ co wideo/poster */}
                                {useImage && image?.src && (
                                    <div className="absolute inset-0 h-full w-full z-10">
                                        <Image
                                            src={image.src}
                                            alt={backgroundAlt}
                                            fill
                                            priority
                                            // @ts-ignore
                                            fetchPriority="high"
                                            className="object-cover object-center"
                                            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 90vw, 1800px"
                                        />
                                    </div>
                                )}

                                {/* WARSTWA 2 (WIERZCH): Poster MUX (przed startem wideo) */}
                                {useVideo && posterUrl && (
                                    <div
                                        className="absolute inset-0 h-full w-full z-20 pointer-events-none"
                                        style={{
                                            opacity: isVideoPlaying ? 0 : 1,
                                            transition: "opacity 0.8s ease-in-out"
                                        }}
                                    >
                                        <Image
                                            src={posterUrl}
                                            alt={backgroundAlt}
                                            fill
                                            priority
                                            // @ts-ignore
                                            fetchPriority="high"
                                            unoptimized
                                            className="object-cover"
                                            sizes="100vw"
                                        />
                                    </div>
                                )}

                                {/* Overlay */}
                                <div
                                    className="absolute inset-0 pointer-events-none z-30"
                                    style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
                                />

                                {/* Mobile: wysokość = treść. Desktop: treść na dole dużego hero */}
                                <div className="relative z-40 flex flex-col gap-6 bg-gradient-to-t from-black/60 to-transparent p-6 md:absolute md:inset-x-0 md:bottom-0 md:p-10 lg:p-14">
                                    {breadcrumbs && breadcrumbs.length > 0 && (
                                        <m.div variants={slideUp} custom={-0.5} className="will-change-transform">
                                            <Breadcrumbs
                                                items={breadcrumbs}
                                                truncateLastOnMobile
                                                className="text-white/90 [&_a]:text-white/90 [&_a:hover]:underline [&_a:hover]:underline-offset-4 [&_.text-neutral-400]:text-white/60"
                                            />
                                        </m.div>
                                    )}
                                    <div className="grid items-end gap-4 md:grid-cols-1 lg:grid-cols-[11fr_9fr] lg:gap-6">
                                        <m.h1 variants={slideUp} custom={0} className="max-w-[50rem] text-3xl font-semibold uppercase leading-[1.2] will-change-transform sm:text-4xl md:text-[3.6rem] md:leading-[1.02] lg:pr-16">
                                            {title}
                                        </m.h1>
                                        <div className="flex flex-col items-start gap-6 text-left md:pl-0 lg:min-w-0 lg:pl-0">
                                            {!!description && (
                                                <m.p variants={slideUp} custom={1} className="max-w-md text-primary md:text-md opacity-90 will-change-transform">
                                                    {description}
                                                </m.p>
                                            )}
                                            <m.div variants={slideUp} custom={2} className="w-fit will-change-transform">
                                                {useLandingButton ? (
                                                    <ButtonLandingPage label={buttonTitle} />
                                                ) : (
                                                    <CtaButton ctaLabel={buttonTitle} />
                                                )}
                                            </m.div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </m.div>
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
}